import React, { useEffect, useState } from 'react';
import axios from 'axios';
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import ManagerSidebar from '../components/Sidebar/ManagerSidebar';

import "./AllMaterials.css";

import { useNavigate } from 'react-router-dom';

import {
    FaEdit,
    FaTrash,
    FaPlus,
    FaArrowCircleDown,
    FaPrint
} from "react-icons/fa";

function AllMaterials() {

    const navigate = useNavigate();

    const [materials, setMaterials] = useState([]);
    const [selectedMaterial, setSelectedMaterial] = useState("ALL");
    const [filteredMaterials, setFilteredMaterials] = useState([]);

    const [editData, setEditData] = useState({
        materialname: "",
        instock: "",
        inhouse: ""
    });

    const [editingId, setEditingId] = useState(null);

    // Keep your dashboard reading your main materials database on load
    useEffect(() => {
        getMaterials();
    }, []);

    useEffect(() => {
        if (selectedMaterial === "ALL") {
            setFilteredMaterials(materials);
        } else {
            const filtered = materials.filter(
                (item) => item.materialname === selectedMaterial
            );
            setFilteredMaterials(filtered);
        }
    }, [selectedMaterial, materials]);

    const getMaterials = async () => {
        try {
            const response = await axios.get(
                'https://ikaze-thouse-backend.vercel.app/api/v1/materials/getAll'
            );
            setMaterials(response.data.data);
        } catch (error) {
            console.log(error);
        }
    };

    const handleDelete = async (id) => {
        const confirmDelete = window.confirm(
            "Are you sure you want to delete this material?"
        );

        if (!confirmDelete) return;

        try {
            await axios.delete(
                `https://ikaze-thouse-backend.vercel.app/api/v1/materials/delete/${id}`
            );
            alert("Material Deleted Successfully");
            getMaterials();
        } catch (error) {
            console.log(error);
            alert("Failed to Delete Material");
        }
    };

    const handleEditClick = (item) => {
        setEditingId(item.material_id);
        setEditData({
            materialname: item.materialname,
            instock: item.instock,
            inhouse: item.inhouse
        });
    };

    const handleUpdate = async () => {
        try {
            await axios.put(
                `https://ikaze-thouse-backend.vercel.app/api/v1/materials/update/${editingId}`,
                editData
            );
            alert("Material Updated Successfully");
            setEditingId(null);
            getMaterials();
        } catch (error) {
            console.log(error);
            alert("Failed to Update Material");
        }
    };

    // PDF REPORT GENERATION WITH ASYNC LOCATION FETCHING
    const generateReport = async () => {
        let locationLookups = [];
        
        // Fetch stockout history background data to link stocklocation mapping
        try {
            const historyRes = await axios.get(
                'https://ikaze-thouse-backend.vercel.app/api/v1/materials/stockout/history'
            );
            if (historyRes.data && historyRes.data.data) {
                locationLookups = historyRes.data.data;
            }
        } catch (err) {
            console.log("Could not look up background stock locations:", err);
        }

        const doc = new jsPDF();

        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();

        // ==================================
        // COMPANY HEADER
        // ==================================
        doc.setFillColor(20, 41, 90);
        doc.rect(0, 0, pageWidth, 30, "F");

        doc.setTextColor(255, 255, 255);
        doc.setFontSize(20);
        doc.setFont(undefined, "bold");
        doc.text("GKM INVEST", pageWidth / 2, 12, { align: "center" });

        doc.setFontSize(11);
        doc.setFont(undefined, "normal");
        doc.text(
            "Materials Management System",
            pageWidth / 2,
            21,
            { align: "center" }
        );

        // ==================================
        // REPORT TITLE
        // ==================================
        doc.setTextColor(20, 41, 90);
        doc.setDrawColor(20, 41, 90);
        doc.roundedRect(
            14,
            38,
            pageWidth - 28,
            18,
            2,
            2
        );

        doc.setFontSize(16);
        doc.setFont(undefined, "bold");
        doc.text(
            "MATERIALS INVENTORY REPORT",
            pageWidth / 2,
            50,
            { align: "center" }
        );

        let y = 70;

        // ==================================
        // REPORT INFORMATION
        // ==================================
        doc.setFont(undefined, "normal");
        doc.setFontSize(11);
        doc.setTextColor(0, 0, 0);

        doc.text(
            `Generated Date: ${new Date().toLocaleString()}`,
            14,
            y
        );

        y += 8;
        doc.text(
            `Selected Material: ${selectedMaterial}`,
            14,
            y
        );

        y += 8;
        doc.text(
            `Total Records: ${filteredMaterials.length}`,
            14,
            y
        );

        // ==================================
        // CALCULATIONS
        // ==================================
        const totalStock = filteredMaterials.reduce(
            (total, item) => total + Number(item.instock || 0),
            0
        );

        const totalHouse = filteredMaterials.reduce(
            (total, item) => total + Number(item.inhouse || 0),
            0
        );

        const totalQty = filteredMaterials.reduce(
            (total, item) => total + Number(item.quantity || 0),
            0
        );

        const totalOther = filteredMaterials.reduce(
            (total, item) => {
                const qty = Number(item.quantity || 0);
                const stock = Number(item.instock || 0);
                const house = Number(item.inhouse || 0);
                const other = qty - (stock + house);
                return total + (other > 0 ? other : 0);
            },
            0
        );

        y += 15;

        // ==================================
        // SUMMARY SECTION
        // ==================================
        doc.setFillColor(240, 244, 248);
        doc.roundedRect(
            14,
            y,
            pageWidth - 28,
            32,
            3,
            3,
            "F"
        );

        doc.setFont(undefined, "bold");
        doc.setFontSize(12);
        doc.setTextColor(20, 41, 90);
        doc.text("REPORT SUMMARY", 18, y + 8);

        doc.setFont(undefined, "normal");
        doc.setTextColor(0, 0, 0);
        doc.text(`Total In Stock : ${totalStock}`, 20, y + 18);
        doc.text(`Total In House : ${totalHouse}`, 80, y + 18);
        doc.text(`Total Other Place : ${totalOther}`, 140, y + 18);
        doc.text(`Total Quantity : ${totalQty}`, 20, y + 27);

        y += 45;

        // ==================================
        // MATERIALS SECTION TITLE
        // ==================================
        doc.setFillColor(20, 41, 90);
        doc.rect(
            14,
            y - 5,
            pageWidth - 28,
            10,
            "F"
        );

        doc.setTextColor(255, 255, 255);
        doc.setFont(undefined, "bold");
        doc.setFontSize(12);
        doc.text(
            "MATERIAL DETAILS",
            18,
            y + 2
        );

        y += 18;

        // ==================================
        // MATERIALS DETAILS LOOP
        // ==================================
        filteredMaterials.forEach((item, index) => {
            const stock = Number(item.instock || 0);
            const house = Number(item.inhouse || 0);
            const qty = Number(item.quantity || 0);
            const other = Math.max(qty - (stock + house), 0);

            // Dynamically match the material by ID or name inside your background stockout history array
            const matchedHistoryItem = locationLookups.find(
                (historyItem) => historyItem.material_id === item.material_id || historyItem.materialname === item.materialname
            );
            
            // Assign matched location text, fallback to item level property, or print N/A
            const otherSpec = matchedHistoryItem?.stocklocation || item.stocklocation || "N/A";

            if (y > pageHeight - 55) {
                doc.addPage();
                y = 20;
            }

            // MATERIAL CONTAINER
            doc.setFillColor(250, 250, 252);
            doc.roundedRect(
                14,
                y - 5,
                pageWidth - 28,
                42,
                3,
                3,
                "F"
            );

            doc.setDrawColor(220, 220, 220);
            doc.roundedRect(
                14,
                y - 5,
                pageWidth - 28,
                42,
                3,
                3
            );

            // MATERIAL NAME
            doc.setFont(undefined, "bold");
            doc.setFontSize(13);
            doc.setTextColor(20, 41, 90);
            doc.text(
                `${index + 1}. ${item.materialname ? item.materialname.toUpperCase() : "UNKNOWN MATERIAL"}`,
                18,
                y + 4
            );

            // BLUE SEPARATOR LINE
            doc.setDrawColor(37, 99, 235);
            doc.setLineWidth(0.5);
            doc.line(
                18,
                y + 8,
                pageWidth - 22,
                y + 8
            );

            // ==================================
            // FIRST ROW
            // ==================================
            // IN STOCK
            doc.setFont(undefined, "bold");
            doc.setTextColor(37, 99, 235);
            doc.setFontSize(10);
            doc.text("In Stock:", 22, y + 18);

            doc.setFont(undefined, "normal");
            doc.setTextColor(0, 0, 0);
            doc.text(`${stock}`, 42, y + 18);

            // IN HOUSE
            doc.setFont(undefined, "bold");
            doc.setTextColor(16, 185, 129);
            doc.text("In House:", 80, y + 18);

            doc.setFont(undefined, "normal");
            doc.setTextColor(0, 0, 0);
            doc.text(`${house}`, 102, y + 18);

            // OTHER PLACE
            doc.setFont(undefined, "bold");
            doc.setTextColor(245, 158, 11);
            doc.text("Other Place:", 135, y + 18);

            doc.setFont(undefined, "normal");
            doc.setTextColor(0, 0, 0);
            doc.text(`${other}`, 165, y + 18);

            // SPECIFICATION IN OTHER PLACE
            doc.setFont(undefined, "bold");
            doc.setTextColor(245, 158, 11);
            doc.text("Spec. Location:", 135, y + 26);

            doc.setFont(undefined, "normal");
            doc.setTextColor(0, 0, 0);
            doc.text(`${otherSpec}`, 165, y + 26);

            // ==================================
            // SECOND ROW
            // ==================================
            // TOTAL QUANTITY
            doc.setFont(undefined, "bold");
            doc.setTextColor(99, 102, 241);
            doc.text("Total Quantity:", 22, y + 34);

            doc.setFont(undefined, "normal");
            doc.setTextColor(0, 0, 0);
            doc.text(`${qty}`, 55, y + 34);

            // CREATED DATE
            doc.setFont(undefined, "bold");
            doc.setTextColor(107, 114, 128);
            doc.text("Created Date:", 90, y + 34);

            doc.setFont(undefined, "normal");
            doc.setTextColor(0, 0, 0);
            doc.text(
                item.created_at
                    ? new Date(item.created_at).toLocaleDateString()
                    : "N/A",
                120,
                y + 34
            );

            // SMALL STATUS LINE
            doc.setDrawColor(230, 230, 230);
            doc.line(
                22,
                y + 38,
                pageWidth - 25,
                y + 38
            );

            y += 50;
        });

        // ==================================
        // FOOTER FOR ALL PAGES
        // ==================================
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setDrawColor(220);
            doc.line(
                14,
                pageHeight - 15,
                pageWidth - 14,
                pageHeight - 15
            );

            doc.setFontSize(9);
            doc.setTextColor(120);
            doc.text(
                "Generated by GKM INVEST",
                14,
                pageHeight - 8
            );

            doc.text(
                `Page ${i} of ${pageCount}`,
                pageWidth - 30,
                pageHeight - 8
            );
        }

        // ==================================
        // DOWNLOAD PDF
        // ==================================
        doc.save(`materials_report_${Date.now()}.pdf`);
    };

    return (
        <div className="all-materials-container">
            <ManagerSidebar />

            <div className="materials-content">
                {/* HEADER */}
                <div className="top-header">
                    <h2 className="materials-title">
                        Materials Dashboard
                    </h2> 

                    <div className="header-buttons">
                        <button
                            className="add-btn"
                            onClick={() => navigate('/add-Materials')}
                        >
                            <FaPlus />
                            Add Materials
                        </button>

                        <button
                            className="stockout-btn"
                            onClick={() => navigate('/stock_out')}
                        >
                            <FaArrowCircleDown />
                            Stock Out
                        </button>
                    </div>
                </div>

                {/* REPORT FILTER */}
                <div
                    className="report-filter"
                    style={{
                        display: "flex",
                        width: "600px",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: "15px",
                        marginBottom: "25px",
                        padding: "18px",
                        background: "#ffffff",
                        borderRadius: "12px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.08)"
                    }}
                >
                    <select
                        value={selectedMaterial}
                        onChange={(e) => setSelectedMaterial(e.target.value)}
                        className="filter-dropdown"
                        style={{
                            flex: 1,
                            padding: "12px 15px",
                            borderRadius: "10px",
                            border: "1px solid #d1d5db",
                            fontSize: "15px",
                            outline: "none",
                            background: "#f9fafb",
                            cursor: "pointer",
                            fontWeight: "500"
                        }}
                    >
                        <option value="ALL">
                            All Materials
                        </option>
                        {[...new Set(materials.map(item => item.materialname).filter(Boolean))].map((name, index) => (
                            <option key={index} value={name}>
                                {name}
                            </option>
                        ))}
                    </select>

                    <button
                        className="report-btn"
                        onClick={generateReport}
                        style={{
                            background: "#4B5563",
                            color: "#fff",
                            border: "none",
                            padding: "12px 20px",
                            borderRadius: "10px",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            cursor: "pointer",
                            fontWeight: "600",
                            fontSize: "15px",
                            transition: "0.3s"
                        }}
                    >
                        <FaPrint />
                        Generate PDF
                    </button>
                </div>

                {/* DASHBOARD CARDS */}
                <div className="dashboard-cards">
                    <div className="card">
                        <h3>Total Recorded Items</h3>
                        <p>{filteredMaterials.length}</p>
                    </div>

                    <div className="card">
                        <h3>Total In Stock</h3>
                        <p>
                            {filteredMaterials.reduce(
                                (total, item) => total + Number(item.instock || 0),
                                0
                            )}
                        </p>
                    </div>

                    <div className="card">
                        <h3>Total In Other</h3>
                        <p>
                            {filteredMaterials.reduce(
                                (total, item) => {
                                    const quantity = Number(item.quantity || 0);
                                    const instock = Number(item.instock || 0);
                                    const inhouse = Number(item.inhouse || 0);
                                    const other = quantity - (instock + inhouse);
                                    return total + (other > 0 ? other : 0);
                                },
                                0
                            )}
                        </p>
                    </div>

                    <div className="card">
                        <h3>Total In House</h3>
                        <p>
                            {filteredMaterials.reduce(
                                (total, item) => total + Number(item.inhouse || 0),
                                0
                            )}
                        </p>
                    </div>
                </div>

                {/* EDIT FORM */}
                {editingId && (
                    <div className="edit-form">
                        <h3>Edit Material</h3>
                        <input
                            type="text"
                            placeholder="Material Name"
                            value={editData.materialname}
                            onChange={(e) =>
                                setEditData({
                                    ...editData,
                                    materialname: e.target.value
                                })
                            }
                        />

                        <input
                            type="number"
                            placeholder="In Stock"
                            value={editData.instock}
                            onChange={(e) =>
                                setEditData({
                                    ...editData,
                                    instock: e.target.value
                                })
                            }
                        />

                        <input
                            type="number"
                            placeholder="In House"
                            value={editData.inhouse}
                            onChange={(e) =>
                                setEditData({
                                    ...editData,
                                    inhouse: e.target.value
                                })
                            }
                        />

                        <div className="edit-buttons">
                            <button
                                className="save-btn"
                                onClick={handleUpdate}
                            >
                                Save Changes
                            </button>

                            <button
                                className="cancel-btn"
                                onClick={() => setEditingId(null)}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}

                {/* MATERIALS TABLE */}
                <div className="materials-card">
                    <table className="materials-table">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Material Name</th>
                                <th>In Stock</th>
                                <th>In House</th>
                                <th>Other Place</th>
                                <th>Total Quantity</th>
                                <th>Created Date</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredMaterials.map((item) => {
                                const instock = Number(item.instock || 0);
                                const inhouse = Number(item.inhouse || 0);
                                const quantity = Number(item.quantity || 0);
                                const other = quantity - (instock + inhouse);
                                const safeOther = other > 0 ? other : 0;

                                return (
                                    <tr key={item.material_id}>
                                        <td>{item.material_id}</td>
                                        <td>{item.materialname}</td>
                                        <td>
                                            <span className="stock-badge">
                                                {instock}
                                            </span>
                                        </td>
                                        <td>
                                            <span className="house-badge">
                                                {inhouse}
                                            </span>
                                        </td>
                                        <td>
                                            <span className="other-badge">
                                                {safeOther}
                                            </span>
                                        </td>
                                        <td>
                                            <span className="total-badge">
                                                {quantity}
                                            </span>
                                        </td>
                                        <td>
                                            {item.created_at
                                                ? new Date(item.created_at).toLocaleDateString()
                                                : "N/A"}
                                        </td>
                                        <td>
                                            <div className="action-buttons">
                                                <button
                                                    className="edit-btn"
                                                    onClick={() => handleEditClick(item)}
                                                >
                                                    <FaEdit />
                                                    Edit
                                                </button>

                                                <button
                                                    className="delete-btn"
                                                    onClick={() => handleDelete(item.material_id)}
                                                >
                                                    <FaTrash />
                                                    Delete
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default AllMaterials;